import React from 'react';
import TopToolbar from '../../global/subcomponents/topToolbar/TopToolbar';
import {
  IMPORTED_FILES_MARKETPLACES,
  IMPORTED_FILES_TITLE,
  MARKETPLACE_SUMMARY_PAGES,
} from './constants';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import RefreshIcon from '@material-ui/icons/Refresh';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import FolderOffOutlinedIcon from '@mui/icons-material/FolderOffOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import Utils from '../../Utils/Utils';
import ImportedFileDetail from './ImportedFileDetail';
import './ImportedFiles.css';

class ImportedFiles extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      marketplacesData: [], // [{ marketplace, fileCount, files: [{filename, importDate, rowCount}] }]
      activeMarketplace: null,

      // detail view
      selectedFile: null, // { marketplace, filename, headers, rows }
      detailLoading: false,

      // rename dialog
      renameOpen: false,
      renameValue: '',
      renameTarget: null, // { marketplace, filename }
      renameSaving: false,

      // delete dialog
      deleteOpen: false,
      deleteTarget: null, // { marketplace, filename, rowCount }
      deleting: false,
    };
  }

  componentDidMount() {
    this.loadFiles();
  }

  loadFiles = (preserveActive = true) => {
    this.setState({ loading: true });
    this.props.listImportedFiles((status, data) => {
      if (status === 'success') {
        const marketplacesData = Array.isArray(data) ? data : [];
        const tabs = this.computeTabs(marketplacesData);
        const activeMarketplace =
          preserveActive && this.state.activeMarketplace
            ? this.state.activeMarketplace
            : tabs[0];
        this.setState({ marketplacesData, activeMarketplace, loading: false });
      } else {
        this.setState({ loading: false });
        this.props.customFeedBackBar('error', data || 'Failed to load imported files.');
      }
    });
  };

  // Always show the known marketplaces as tabs (even with no files), then
  // append any extra marketplaces the server returned that aren't in the list.
  computeTabs = (marketplacesData) => {
    const present = marketplacesData.map((m) => m.marketplace);
    const extras = present
      .filter((m) => !IMPORTED_FILES_MARKETPLACES.includes(m))
      .sort();
    return [...IMPORTED_FILES_MARKETPLACES, ...extras];
  };

  getFilesFor = (marketplace) => {
    const entry = this.state.marketplacesData.find(
      (m) => m.marketplace === marketplace
    );
    return entry ? entry.files : [];
  };

  handleTabChange = (event, value) => {
    this.setState({ activeMarketplace: value });
  };

  openFile = (marketplace, filename) => {
    this.setState({
      detailLoading: true,
      selectedFile: { marketplace, filename, headers: [], rows: [] },
    });
    this.props.getImportedFileRows({ marketplace, filename }, (status, data) => {
      if (status === 'success') {
        this.setState({
          detailLoading: false,
          selectedFile: {
            marketplace: data.marketplace,
            filename: data.filename,
            headers: data.headers || [],
            rows: data.rows || [],
          },
        });
      } else {
        this.setState({ detailLoading: false, selectedFile: null });
        this.props.customFeedBackBar('error', data || 'Failed to load file rows.');
      }
    });
  };

  closeDetail = () => {
    this.setState({ selectedFile: null });
  };

  // ---- rename ----------------------------------------------------
  openRename = (marketplace, filename) => {
    this.setState({
      renameOpen: true,
      renameValue: filename,
      renameTarget: { marketplace, filename },
    });
  };

  closeRename = () => {
    if (this.state.renameSaving) return;
    this.setState({ renameOpen: false, renameValue: '', renameTarget: null });
  };

  confirmRename = () => {
    const { renameTarget, renameValue } = this.state;
    const newFilename = (renameValue || '').trim();
    if (!renameTarget || !newFilename || newFilename === renameTarget.filename) {
      this.closeRename();
      return;
    }
    this.setState({ renameSaving: true });
    this.props.renameImportedFile(
      {
        marketplace: renameTarget.marketplace,
        oldFilename: renameTarget.filename,
        newFilename,
      },
      (status, message) => {
        this.setState({ renameSaving: false });
        if (status === 'success') {
          this.applyRenameLocally(
            renameTarget.marketplace,
            renameTarget.filename,
            newFilename
          );
          this.setState({ renameOpen: false, renameValue: '', renameTarget: null });
          this.props.customFeedBackBar('success', message || 'File renamed.');
        } else {
          this.props.customFeedBackBar('error', message || 'Failed to rename file.');
        }
      }
    );
  };

  applyRenameLocally = (marketplace, oldFilename, newFilename) => {
    const marketplacesData = this.state.marketplacesData.map((m) => {
      if (m.marketplace !== marketplace) return m;
      return {
        ...m,
        files: m.files.map((f) =>
          f.filename === oldFilename ? { ...f, filename: newFilename } : f
        ),
      };
    });
    const selectedFile =
      this.state.selectedFile &&
      this.state.selectedFile.marketplace === marketplace &&
      this.state.selectedFile.filename === oldFilename
        ? { ...this.state.selectedFile, filename: newFilename }
        : this.state.selectedFile;
    this.setState({ marketplacesData, selectedFile });
  };

  // ---- delete ----------------------------------------------------
  openDelete = (marketplace, filename, rowCount) => {
    this.setState({
      deleteOpen: true,
      deleteTarget: { marketplace, filename, rowCount },
    });
  };

  closeDelete = () => {
    if (this.state.deleting) return;
    this.setState({ deleteOpen: false, deleteTarget: null });
  };

  confirmDelete = () => {
    const { deleteTarget } = this.state;
    if (!deleteTarget) return;
    this.setState({ deleting: true });
    this.props.deleteImportedFile(
      {
        marketplace: deleteTarget.marketplace,
        filename: deleteTarget.filename,
      },
      (status, message) => {
        this.setState({ deleting: false });
        if (status === 'success') {
          this.applyDeleteLocally(deleteTarget.marketplace, deleteTarget.filename);
          this.setState({
            deleteOpen: false,
            deleteTarget: null,
            selectedFile: null,
          });
          this.props.customFeedBackBar('success', message || 'File deleted.');
          if (typeof this.props.onImportedFilesChanged === 'function') {
            this.props.onImportedFilesChanged();
          }
        } else {
          this.props.customFeedBackBar('error', message || 'Failed to delete file.');
        }
      }
    );
  };

  applyDeleteLocally = (marketplace, filename) => {
    const marketplacesData = this.state.marketplacesData
      .map((m) => {
        if (m.marketplace !== marketplace) return m;
        const files = m.files.filter((f) => f.filename !== filename);
        return { ...m, files, fileCount: files.length };
      });
    this.setState({ marketplacesData });
  };

  getContextualMenuItems = () => [
    {
      title: 'Back',
      icon: <KeyboardBackspaceIcon />,
      onClick: () => this.props.setCurrentPage(MARKETPLACE_SUMMARY_PAGES.TABLE),
    },
    {
      title: 'Refresh',
      icon: <RefreshIcon />,
      onClick: () => this.loadFiles(true),
    },
  ];

  renderFileList = () => {
    const files = this.getFilesFor(this.state.activeMarketplace);

    if (!files.length) {
      return (
        <div className="imported-files-empty">
          <FolderOffOutlinedIcon className="imported-files-empty-icon" />
          <div className="imported-files-empty-title">No imported files</div>
          <div className="imported-files-empty-sub">
            There are no files imported for {this.state.activeMarketplace} yet.
          </div>
        </div>
      );
    }

    return (
      <div className="imported-files-list">
        {files.map((file) => (
          <div
            key={file.filename}
            className="imported-file-card"
            onClick={() =>
              this.openFile(this.state.activeMarketplace, file.filename)
            }
          >
            <div className="imported-file-icon">
              <InsertDriveFileOutlinedIcon />
            </div>
            <div className="imported-file-meta">
              <div className="imported-file-name" title={file.filename}>
                {file.filename}
              </div>
              <div className="imported-file-sub">
                <span>Imported {Utils.formatDateTime(file.importDate)}</span>
              </div>
            </div>
            <span className="imported-file-rowcount">
              {file.rowCount} {file.rowCount === 1 ? 'row' : 'rows'}
            </span>
            <span className="imported-file-chevron">
              <ChevronRightIcon />
            </span>
          </div>
        ))}
      </div>
    );
  };

  renderListPage = () => {
    const tabs = this.computeTabs(this.state.marketplacesData);

    return (
      <React.Fragment>
        <TopToolbar
          pageName={IMPORTED_FILES_TITLE}
          menuItems={this.getContextualMenuItems()}
        />

        {this.state.loading ? (
          <div className="imported-files-loading">
            <CircularProgress sx={{ color: '#005e73' }} />
          </div>
        ) : (
          <div className="imported-files-root">
            <div className="imported-files-tabs-wrap">
              <Tabs
                value={this.state.activeMarketplace || tabs[0]}
                onChange={this.handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                TabIndicatorProps={{ style: { backgroundColor: '#005e73' } }}
              >
                {tabs.map((mp) => {
                  const count = this.getFilesFor(mp).length;
                  return (
                    <Tab
                      key={mp}
                      value={mp}
                      className="imported-files-tab"
                      label={
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          {mp}
                          <span className="imported-files-tab-count">{count}</span>
                        </span>
                      }
                    />
                  );
                })}
              </Tabs>
            </div>

            {this.renderFileList()}
          </div>
        )}
      </React.Fragment>
    );
  };

  render() {
    const { selectedFile } = this.state;

    return (
      <React.Fragment>
        {selectedFile ? (
          <ImportedFileDetail
            marketplace={selectedFile.marketplace}
            filename={selectedFile.filename}
            headers={selectedFile.headers}
            rows={selectedFile.rows}
            loading={this.state.detailLoading}
            onBack={this.closeDetail}
            onRename={() =>
              this.openRename(selectedFile.marketplace, selectedFile.filename)
            }
            onDelete={() =>
              this.openDelete(
                selectedFile.marketplace,
                selectedFile.filename,
                selectedFile.rows.length
              )
            }
          />
        ) : (
          this.renderListPage()
        )}

        {/* Rename dialog */}
        <Dialog
          open={this.state.renameOpen}
          onClose={this.closeRename}
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 460,
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 600, fontSize: '1.125rem', pt: 2.5 }}>
            Rename imported file
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <DialogContentText sx={{ mb: 2, fontSize: '0.9rem' }}>
              Update the name used to identify this import. The underlying data
              is not changed.
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              label="File name"
              value={this.state.renameValue}
              disabled={this.state.renameSaving}
              onChange={(e) => this.setState({ renameValue: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') this.confirmRename();
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
            <Button
              onClick={this.closeRename}
              disabled={this.state.renameSaving}
              sx={{
                color: 'text.secondary',
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={this.confirmRename}
              variant="contained"
              disabled={
                this.state.renameSaving ||
                !this.state.renameValue.trim() ||
                (this.state.renameTarget &&
                  this.state.renameValue.trim() ===
                    this.state.renameTarget.filename)
              }
              sx={{
                bgcolor: '#005E73',
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#004a5c', boxShadow: 'none' },
              }}
            >
              {this.state.renameSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirmation */}
        <Dialog
          open={this.state.deleteOpen}
          onClose={this.closeDelete}
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 460,
              maxWidth: 540,
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          <DialogTitle
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 2.5 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'rgba(211, 47, 47, 0.1)',
                color: '#d32f2f',
                flexShrink: 0,
              }}
            >
              <WarningAmberRoundedIcon />
            </Box>
            <Box sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
              Delete imported file
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <DialogContentText sx={{ color: 'text.primary', fontSize: '0.95rem' }}>
              This will permanently delete{' '}
              <strong>{this.state.deleteTarget?.filename}</strong>
              {this.state.deleteTarget?.marketplace
                ? ` from ${this.state.deleteTarget.marketplace}`
                : ''}{' '}
              and remove all{' '}
              {this.state.deleteTarget?.rowCount != null
                ? `${this.state.deleteTarget.rowCount} `
                : ''}
              rows of data it imported. This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
            <Button
              onClick={this.closeDelete}
              disabled={this.state.deleting}
              sx={{
                color: 'text.secondary',
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={this.confirmDelete}
              variant="contained"
              disabled={this.state.deleting}
              sx={{
                bgcolor: '#d32f2f',
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#b71c1c', boxShadow: 'none' },
              }}
            >
              {this.state.deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>
    );
  }
}

export default ImportedFiles;
